import { useGetStaticSearchCandidates } from "apollo/hooks/queries";
import clsx from "clsx";
import { CANDIDATES_STATUS_SUCCESS } from "core/consts";
import { isPrintMode } from "core/model/utils/urls";
import type { Auction } from "core/types";
import SpinnerPage from "ds_legacy/components/Spinner";
import {
  shouldDisplayCandidates,
  useCandidates,
} from "dsl/atoms/SearchCandidates";
import { PatientContentLayout } from "dsl/organisms/PatientContentLayout";
import PatientMenu, { isValidPatient } from "dsl/organisms/PatientMenu";
import { useExtractedPatientFields } from "dsl/organisms/PatientMenu/useExtractedPatientFields";
import PatientNavigation from "dsl/organisms/PatientNavigation";
import type { PatientAuctionContext } from "entries/sender/Router/PatientRouteLayout";
import { Suspense, useEffect } from "react";
import { Outlet } from "react-router-dom";

/**
 * Syncs search candidates for the auction into SearchCandidates state.
 * Only mounted when not in print so queries/effects do not run for print.
 */
function PatientAuctionSearchCandidatesSync({ auction }: { auction: Auction }) {
  const [getSearchCandidates] = useGetStaticSearchCandidates({
    auctionId: auction.id,
  });
  const extracted = useExtractedPatientFields(auction);
  const { setCandidates } = useCandidates();

  useEffect(() => {
    (async () => {
      if (
        isValidPatient(auction.patient, auction) &&
        shouldDisplayCandidates(auction) &&
        auction.candidates_status === CANDIDATES_STATUS_SUCCESS
      ) {
        const result = await getSearchCandidates();
        if (result?.data) {
          setCandidates({
            candidates: result.data.searchCandidates?.count ?? 0,
            patientId: auction.patient?.id ?? -1,
          });
        }
      } else {
        setCandidates({
          candidates: undefined,
          patientId: auction.patient?.id ?? -1,
        });
      }
    })();
  }, [extracted, auction.status, auction.candidates_status]);

  return null;
}

export function PatientAuctionMenu({
  auction,
  patient,
}: PatientAuctionContext) {
  const isPrint = isPrintMode();

  return (
    <>
      {!isPrint && (
        <>
          <PatientAuctionSearchCandidatesSync auction={auction} />
          <PatientMenu auction={auction} patient={patient} />
        </>
      )}
      <div className={clsx({ block: isPrint, "flex flex-col": !isPrint })}>
        <PatientNavigation auction={auction} patient={patient}>
          <PatientContentLayout variant="tabs">
            <Suspense
              fallback={
                <div
                  className="flex flex-1 items-center justify-center"
                  data-testid="patient-tab-content-loading"
                >
                  <SpinnerPage id="patient-tab-content" />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </PatientContentLayout>
        </PatientNavigation>
      </div>
    </>
  );
}
